class WilcoProductShippingEstimator {
  constructor(container) {
    this.container = container;

    this.form = container.querySelector(
      '[data-product-shipping-form]'
    );

    this.zipInput = container.querySelector(
      '[data-product-shipping-zip]'
    );

    this.button = container.querySelector(
      '[data-product-shipping-submit]'
    );

    this.results = container.querySelector(
      '[data-product-shipping-results]'
    );

    this.message = container.querySelector(
      '[data-product-shipping-message]'
    );

    this.sectionId =
      container.dataset.sectionId;

    this.productFormId =
      container.dataset.productFormId;

    this.shopDomain =
      container.dataset.shopDomain;

    this.token =
      container.dataset.storefrontToken;

    this.currency =
      container.dataset.currency || 'USD';

    this.showTotal =
      container.dataset.showTotal === 'true';

    this.showDelivery =
      container.dataset.showDelivery === 'true';

    if (
      !this.form ||
      !this.zipInput ||
      !this.button
    ) {
      return;
    }

    const savedZip =
      localStorage.getItem(
        'wilco_shipping_zip'
      );

    if (savedZip) {
      this.zipInput.value = savedZip;
    }

    this.form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();

        this.calculate();
      }
    );
  }

  getProductForm() {
    return document.getElementById(
      this.productFormId
    );
  }

  getVariantId() {
    const productForm =
      this.getProductForm();

    const variantInput =
      productForm?.querySelector(
        '[name="id"]'
      );

    return variantInput?.value || null;
  }

  getQuantity() {
    const quantityInput =
      document.getElementById(
        `Quantity-${this.sectionId}`
      );

    const quantity =
      parseInt(
        quantityInput?.value || '1',
        10
      );

    return Number.isFinite(quantity)
      ? Math.max(quantity, 1)
      : 1;
  }

  getStateFromZip(zip) {
    const prefix =
      parseInt(
        zip.substring(0, 3),
        10
      );

    if (
      Number.isNaN(prefix)
    ) {
      return null;
    }

    /*
     * U.S. ZIP prefix mapping.
     */

    if (prefix >= 10 && prefix <= 27)
      return 'MA';

    if (prefix >= 28 && prefix <= 29)
      return 'RI';

    if (prefix >= 30 && prefix <= 38)
      return 'NH';

    if (prefix >= 39 && prefix <= 49)
      return 'ME';

    if (prefix >= 50 && prefix <= 59)
      return 'VT';

    if (prefix >= 60 && prefix <= 69)
      return 'CT';

    if (prefix >= 70 && prefix <= 89)
      return 'NJ';

    const ranges = [
      ['NY', 5, 149],
      ['PA', 150, 196],
      ['DE', 197, 199],
      ['DC', 200, 205],
      ['VA', 201, 246],
      ['WV', 247, 268],
      ['NC', 270, 289],
      ['SC', 290, 299],
      ['GA', 300, 319],
      ['FL', 320, 349],
      ['AL', 350, 369],
      ['TN', 370, 385],
      ['MS', 386, 397],
      ['KY', 400, 427],
      ['OH', 430, 459],
      ['IN', 460, 479],
      ['MI', 480, 499],
      ['IA', 500, 528],
      ['WI', 530, 549],
      ['MN', 550, 567],
      ['SD', 570, 577],
      ['ND', 580, 588],
      ['MT', 590, 599],
      ['IL', 600, 629],
      ['MO', 630, 658],
      ['KS', 660, 679],
      ['NE', 680, 693],
      ['LA', 700, 714],
      ['AR', 716, 729],
      ['OK', 730, 749],
      ['TX', 750, 799],
      ['CO', 800, 816],
      ['WY', 820, 831],
      ['ID', 832, 838],
      ['UT', 840, 847],
      ['AZ', 850, 865],
      ['NM', 870, 884],
      ['NV', 889, 898],
      ['CA', 900, 961],
      ['HI', 967, 968],
      ['OR', 970, 979],
      ['WA', 980, 994],
      ['AK', 995, 999]
    ];

    for (
      const [state, min, max]
      of ranges
    ) {
      if (
        prefix >= min &&
        prefix <= max
      ) {
        return state;
      }
    }

    return null;
  }

  async storefrontRequest(
    query,
    variables = {}
  ) {
    const response = await fetch(
      `https://${this.shopDomain}/api/2026-07/graphql.json`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          'X-Shopify-Storefront-Access-Token':
            this.token
        },

        body: JSON.stringify({
          query,
          variables
        })
      }
    );

    const data =
      await response.json();

    if (!response.ok) {
      console.error(
        'Storefront API HTTP error:',
        data
      );

      throw new Error(
        'Storefront API request failed.'
      );
    }

    if (
      data.errors &&
      data.errors.length
    ) {
      console.error(
        'Storefront API GraphQL error:',
        data.errors
      );

      throw new Error(
        data.errors[0].message
      );
    }

    return data.data;
  }

  async createEstimateCart(
    variantId,
    quantity
  ) {
    const mutation = `
      mutation CreateShippingEstimateCart(
        $input: CartInput
      ) {
        cartCreate(input: $input) {
          cart {
            id

            cost {
              subtotalAmount {
                amount
                currencyCode
              }

              totalAmount {
                amount
                currencyCode
              }
            }
          }

          userErrors {
            field
            message
          }
        }
      }
    `;

    const merchandiseId =
      `gid://shopify/ProductVariant/${variantId}`;

    const data =
      await this.storefrontRequest(
        mutation,
        {
          input: {
            lines: [
              {
                merchandiseId,
                quantity
              }
            ]
          }
        }
      );

    const result =
      data.cartCreate;

    if (
      result.userErrors?.length
    ) {
      throw new Error(
        result.userErrors
          .map(error => error.message)
          .join(', ')
      );
    }

    return result.cart;
  }

  async addDestination(
  cartId,
  zip,
  state
) {
  const mutation = `
    mutation AddDeliveryAddress(
      $cartId: ID!
      $addresses: [CartSelectableAddressInput!]!
    ) {
      cartDeliveryAddressesAdd(
        cartId: $cartId
        addresses: $addresses
      ) {
        cart {
          id

          cost {
            subtotalAmount {
              amount
              currencyCode
            }
          }
        }

        userErrors {
          field
          message
        }
      }
    }
  `;

  const data =
    await this.storefrontRequest(
      mutation,
      {
        cartId,

        addresses: [
          {
            address: {
              deliveryAddress: {
                countryCode: 'US',
                provinceCode: state,
                zip
              }
            },
            selected: true
          }
        ]
      }
    );

  const result =
    data.cartDeliveryAddressesAdd;

  if (result.userErrors?.length) {
    throw new Error(
      result.userErrors
        .map(error => error.message)
        .join(', ')
    );
  }

  return result.cart;
}
async getCarrierRates(cartId) {
  const query = `
    query GetCarrierRates($cartId: ID!) {
      cart(id: $cartId) {
        id

        cost {
          subtotalAmount {
            amount
            currencyCode
          }
        }

        ...CarrierRates @defer
      }
    }

    fragment CarrierRates on Cart {
      deliveryGroups(
        first: 10
        withCarrierRates: true
      ) {
        edges {
          node {
            id

            deliveryOptions {
              handle
              title

              estimatedCost {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetch(
    `https://${this.shopDomain}/api/2026-07/graphql.json`,
    {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token':
          this.token
      },

      body: JSON.stringify({
        query,
        variables: {
          cartId
        }
      })
    }
  );

  if (!response.ok) {
    const errorText =
      await response.text();

    console.error(
      'Carrier rate HTTP error:',
      response.status,
      errorText
    );

    throw new Error(
      'Carrier rate request failed.'
    );
  }

  const contentType =
    response.headers.get('content-type') || '';

  /*
   * Shopify may return everything as normal JSON
   * if it decides not to stream the deferred result.
   */
  if (
    contentType.includes(
      'application/json'
    )
  ) {
    const result =
      await response.json();

    if (result.errors?.length) {
      console.error(
        'Carrier rate GraphQL error:',
        result.errors
      );

      throw new Error(
        result.errors[0].message
      );
    }

    return result.data?.cart;
  }

  /*
   * Carrier-calculated rates normally arrive as
   * multipart/mixed because Shopify requires @defer.
   */
  const responseText =
    await response.text();

  const boundaryMatch =
    contentType.match(
      /boundary="?([^";]+)"?/i
    );

  if (!boundaryMatch) {
    console.error(
      'Unable to find GraphQL multipart boundary:',
      contentType,
      responseText
    );

    throw new Error(
      'Invalid carrier-rate response.'
    );
  }

  const boundary =
    boundaryMatch[1];

  const parts =
    responseText.split(
      `--${boundary}`
    );

  let cart = null;

  for (const part of parts) {
    const jsonStart =
      part.indexOf('{');

    if (jsonStart === -1) {
      continue;
    }

    const jsonText =
      part.slice(jsonStart).trim();

    if (!jsonText) {
      continue;
    }

    let payload;

    try {
      payload =
        JSON.parse(jsonText);
    } catch (error) {
      continue;
    }

    if (payload.errors?.length) {
      console.error(
        'Carrier rate GraphQL error:',
        payload.errors
      );

      throw new Error(
        payload.errors[0].message
      );
    }

    /*
     * First Shopify response:
     * normal cart information.
     */
    if (payload.data?.cart) {
      cart = payload.data.cart;
    }

    /*
     * Deferred response:
     * carrier-calculated deliveryGroups.
     */
    if (
      Array.isArray(
        payload.incremental
      )
    ) {
      payload.incremental.forEach(
        item => {
          if (
            item.data?.deliveryGroups
          ) {
            if (!cart) {
              cart = {};
            }

            cart.deliveryGroups =
              item.data.deliveryGroups;
          }

          if (item.errors?.length) {
            console.error(
              'Deferred carrier rate error:',
              item.errors
            );
          }
        }
      );
    }
  }

  if (!cart) {
    throw new Error(
      'Shopify did not return cart data.'
    );
  }

  return cart;
}

  async calculate() {
    const zip =
      this.zipInput.value.trim();

    if (
      !/^\d{5}(-\d{4})?$/.test(zip)
    ) {
      this.showError(
        'Please enter a valid U.S. ZIP code.'
      );

      return;
    }

    const state =
      this.getStateFromZip(zip);

    if (!state) {
      this.showError(
        'We could not determine the state for this ZIP code.'
      );

      return;
    }

    const variantId =
      this.getVariantId();

    if (!variantId) {
      this.showError(
        'Please select a product option first.'
      );

      return;
    }

    const quantity =
      this.getQuantity();

    localStorage.setItem(
      'wilco_shipping_zip',
      zip
    );

    localStorage.setItem(
      'wilco_shipping_state',
      state
    );

    this.setLoading(true);

    this.results.innerHTML = '';
    this.message.textContent = '';

    try {
      const cart =
        await this.createEstimateCart(
          variantId,
          quantity
        );

      await this.addDestination(
  cart.id,
  zip,
  state
);

/*
 * Destination is now attached.
 * Ask Shopify for live carrier-calculated rates.
 */
const estimatedCart =
  await this.getCarrierRates(
    cart.id
  );

this.render(
  estimatedCart,
  zip,
  state
);

    } catch (error) {
      console.error(
        'Wilco PDP Shipping Error:',
        error
      );

      this.showError(
        'Shipping could not be calculated for this product.'
      );
    } finally {
      this.setLoading(false);
    }
  }

  render(
  cart,
  zip,
  state
) {
  this.results.innerHTML = '';

  const groups =
    cart.deliveryGroups?.edges || [];

  const options = [];

  groups.forEach(edge => {
    const deliveryOptions =
      edge.node?.deliveryOptions || [];

    deliveryOptions.forEach(
      option => options.push(option)
    );
  });

  if (!options.length) {
    this.showError(
      `No shipping options are available for ${zip}.`
    );

    return;
  }

  /*
   * Default to Shopify's first returned rate.
   */
  this.selectedRate = options[0];

  const destination =
    document.createElement('div');

  destination.className =
    'wilco-product-shipping__destination';

  destination.innerHTML = `
    <span>Shipping to</span>
    <strong>${zip}, ${state}</strong>
  `;

  const list =
    document.createElement('div');

  list.className =
    'wilco-product-shipping__rates';

  options.forEach(
    (option, index) => {

      /*
       * Use a label so the entire shipping-rate
       * row is clickable.
       */
      const row =
        document.createElement('label');

      row.className =
        'wilco-product-shipping__rate wilco-product-shipping__rate--selectable';

      const radio =
        document.createElement('input');

      radio.type = 'radio';

      radio.name =
        `wilco-product-shipping-rate-${this.sectionId}`;

      radio.value =
        option.handle || index;

      radio.className =
        'wilco-product-shipping__rate-radio';

      radio.checked =
        index === 0;

      const info =
        document.createElement('div');

      info.className =
        'wilco-product-shipping__rate-info';

      const title =
        document.createElement('strong');

      title.textContent =
        option.title ||
        'Shipping';

      info.appendChild(title);

      if (this.showDelivery) {
        const delivery =
          document.createElement('span');

        delivery.className =
          'wilco-product-shipping__delivery';

        delivery.textContent =
          this.getDeliveryEstimate(
            option.title || ''
          );

        info.appendChild(
          delivery
        );
      }

      const price =
        document.createElement('strong');

      price.className =
        'wilco-product-shipping__price';

      price.textContent =
        this.formatMoney(
          option.estimatedCost.amount,
          option.estimatedCost.currencyCode
        );

      /*
       * Change the selected rate and immediately
       * recalculate the PDP estimate.
       */
      radio.addEventListener(
        'change',
        () => {
          this.selectedRate = option;

          this.renderTotal(
            cart,
            option
          );
        }
      );

      row.appendChild(radio);
      row.appendChild(info);
      row.appendChild(price);

      list.appendChild(row);
    }
  );

  this.results.appendChild(
    destination
  );

  this.results.appendChild(
    list
  );

  /*
   * Render first Shopify rate as the
   * initial estimate.
   */
  if (
    this.showTotal &&
    this.selectedRate
  ) {
    this.renderTotal(
      cart,
      this.selectedRate
    );
  }
}

  renderTotal(
    cart,
    rate
  ) {
    const existingSummary =
  this.results.querySelector(
    '.wilco-product-shipping__summary'
  );

if (existingSummary) {
  existingSummary.remove();
}
    const subtotal =
      parseFloat(
        cart.cost
          .subtotalAmount
          .amount
      );

    const shipping =
      parseFloat(
        rate.estimatedCost.amount
      );

    const total =
      subtotal + shipping;

    const currency =
      rate.estimatedCost
        .currencyCode ||
      this.currency;

    const summary =
      document.createElement('div');

    summary.className =
      'wilco-product-shipping__summary';

    summary.innerHTML = `
      <div class="wilco-product-shipping__summary-row">
        <span>Product subtotal</span>

        <span>
          ${this.formatMoney(
            subtotal,
            currency
          )}
        </span>
      </div>

      <div class="wilco-product-shipping__summary-row">
        <span>Shipping</span>

        <span>
          ${this.formatMoney(
            shipping,
            currency
          )}
        </span>
      </div>

      <div class="wilco-product-shipping__summary-total">
        <strong>
          Estimated Total Before Tax
        </strong>

        <strong>
          ${this.formatMoney(
            total,
            currency
          )}
        </strong>
      </div>
    `;

    this.results.appendChild(
      summary
    );
  }

  formatMoney(
    amount,
    currency = 'USD'
  ) {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency
      }
    ).format(
      Number(amount)
    );
  }

  getDeliveryEstimate(
    shippingName
  ) {
    const name =
      shippingName.toLowerCase();

    let transitMin = 3;
    let transitMax = 7;

    if (
      name.includes('next day') ||
      name.includes('overnight')
    ) {
      transitMin = 1;
      transitMax = 1;

    } else if (
      name.includes('2nd day') ||
      name.includes('2-day') ||
      name.includes('2 day')
    ) {
      transitMin = 2;
      transitMax = 2;

    } else if (
      name.includes('3 day') ||
      name.includes('3-day')
    ) {
      transitMin = 3;
      transitMax = 3;
    }

    /*
     * Wilco processing:
     * 1–2 business days.
     */

    const earliest =
      this.addBusinessDays(
        new Date(),
        1 + transitMin
      );

    const latest =
      this.addBusinessDays(
        new Date(),
        2 + transitMax
      );

    return (
      'Estimated delivery: ' +
      this.formatDate(earliest) +
      ' – ' +
      this.formatDate(latest)
    );
  }

  addBusinessDays(
    date,
    days
  ) {
    const result =
      new Date(date);

    let added = 0;

    while (added < days) {
      result.setDate(
        result.getDate() + 1
      );

      const day =
        result.getDay();

      if (
        day !== 0 &&
        day !== 6
      ) {
        added++;
      }
    }

    return result;
  }

  formatDate(date) {
    return date.toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric'
      }
    );
  }

  setLoading(loading) {
    this.button.disabled =
      loading;

    this.button.textContent =
      loading
        ? 'CALCULATING...'
        : 'ESTIMATE';
  }

  showError(message) {
    this.results.innerHTML = '';

    this.message.textContent =
      message;
  }
}

function initializeWilcoProductShipping() {
  document
    .querySelectorAll(
      '[data-wilco-product-shipping]'
    )
    .forEach(container => {
      if (
        container.dataset
          .initialized === 'true'
      ) {
        return;
      }

      container.dataset.initialized =
        'true';

      new WilcoProductShippingEstimator(
        container
      );
    });
}

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    initializeWilcoProductShipping
  );
} else {
  initializeWilcoProductShipping();
}

document.addEventListener(
  'shopify:section:load',
  initializeWilcoProductShipping
);